import React, { useMemo } from "react";
import { useAnalyses } from "@/context/AnalysisContext";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Sparkles, Trophy, AlertTriangle, Lightbulb } from "lucide-react";

function InsightColumn({ icon: Icon, title, color, items, emptyText }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4" style={{ color }} />
        <h3 className="font-display font-semibold tracking-tight">{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((t, i) => (
            <li key={i} className="text-sm leading-relaxed flex gap-2">
              <span className="text-muted-foreground">•</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function AIInsights() {
  const { analyses } = useAnalyses();

  const { wins, weaknesses, recs } = useMemo(() => {
    const wins = [], weaknesses = [], recs = [];
    analyses.forEach((a) => {
      (a.ai_analysis?.winning_elements || []).forEach((w) => wins.push(w));
      (a.ai_analysis?.weaknesses || []).forEach((w) => weaknesses.push(w));
      (a.ai_analysis?.recommendations || []).forEach((w) => recs.push(w));
    });
    return {
      wins: wins.slice(0, 8),
      weaknesses: weaknesses.slice(0, 8),
      recs: recs.slice(0, 8),
    };
  }, [analyses]);

  if (analyses.length === 0) {
    return (
      <div className="space-y-6">
        <Header />
        <EmptyState
          icon={Sparkles}
          title="No insights yet"
          description="Once you analyze YouTube videos, AI-surfaced patterns across your teardowns will appear here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header />
      <div className="grid lg:grid-cols-3 gap-4">
        <InsightColumn icon={Trophy} color="#10B981" title="What's winning" items={wins} emptyText="No winning elements yet." />
        <InsightColumn icon={AlertTriangle} color="#F59E0B" title="Common weaknesses" items={weaknesses} emptyText="No weaknesses yet." />
        <InsightColumn icon={Lightbulb} color="#38BDF8" title="Recommended tests" items={recs} emptyText="No recommendations yet." />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <p className="overline text-muted-foreground">AI-surfaced patterns</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">AI Insights</h1>
    </div>
  );
}
